type RuntimeEnvKey =
  | "VITE_JAWG_ACCESS_TOKEN"
  | "VITE_IPINFO_TOKEN"
  | "VITE_MAPTILER_KEY"
  | "VITE_TRACESTRACK_KEY"
  | "VITE_THUNDERFOREST_KEY"
  | "VITE_POSTPASS_API_URL"
  | "VITE_PHOTON_API_URL"
  | "VITE_OSM_BASE_URL";

type RuntimeEnv = Partial<Record<RuntimeEnvKey, string | undefined>>;

export interface RuntimeConfig {
  jawgAccessToken: string;
  ipInfoToken: string;
  mapTilerKey: string;
  tracestrackKey: string;
  thunderforestKey: string | undefined;
  postpassApiUrl: string;
  photonApiUrl: string;
  osmBaseUrl: string;
}

const DEFAULT_POSTPASS_API_URL =
  "https://postpass.geofabrik.de/api/0.2/interpreter";
const DEFAULT_PHOTON_API_URL = "https://photon.komoot.io/api?";
const DEFAULT_OSM_BASE_URL = "https://www.openstreetmap.org";

const viteEnv = (import.meta as ImportMeta & { env?: RuntimeEnv }).env ?? {};

export const runtimeConfig: RuntimeConfig = {
  get jawgAccessToken() {
    return requiredText(viteEnv, "VITE_JAWG_ACCESS_TOKEN");
  },
  get ipInfoToken() {
    return requiredText(viteEnv, "VITE_IPINFO_TOKEN");
  },
  get mapTilerKey() {
    return requiredText(viteEnv, "VITE_MAPTILER_KEY");
  },
  get tracestrackKey() {
    return requiredText(viteEnv, "VITE_TRACESTRACK_KEY");
  },
  get thunderforestKey() {
    return optionalText(viteEnv, "VITE_THUNDERFOREST_KEY");
  },
  get postpassApiUrl() {
    return url(viteEnv, "VITE_POSTPASS_API_URL", DEFAULT_POSTPASS_API_URL);
  },
  get photonApiUrl() {
    return url(viteEnv, "VITE_PHOTON_API_URL", DEFAULT_PHOTON_API_URL);
  },
  get osmBaseUrl() {
    return trimTrailingSlashes(
      url(viteEnv, "VITE_OSM_BASE_URL", DEFAULT_OSM_BASE_URL),
    );
  },
};

function requiredText(env: RuntimeEnv, key: RuntimeEnvKey): string {
  const value = optionalText(env, key);
  if (value) return value;
  throw new Error(`Missing required runtime config: ${key}`);
}

function optionalText(env: RuntimeEnv, key: RuntimeEnvKey): string | undefined {
  const value = env[key]?.trim();
  return value ? value : undefined;
}

function url(env: RuntimeEnv, key: RuntimeEnvKey, fallback: string): string {
  const value = optionalText(env, key) ?? fallback;
  try {
    new URL(value);
  } catch {
    throw new Error(`Invalid runtime config URL for ${key}: ${value}`);
  }
  return value;
}

function trimTrailingSlashes(value: string): string {
  return value.replace(/\/+$/, "");
}
